import { type ComponentProps } from 'react'

import { Icon } from '@/components/icons/Icon'
import { IconWithLabel } from '@/components/icons/IconWithLabel'
import { usePage } from '@/components/providers/PageProvider'
import { COLOR_ID_LABELS } from '@/defs/mtg'
import { type RecType } from '@/defs/rec'
import { useCardsRec } from '@/hooks/useCardsRec'
import { getColorId } from '@/utils/cardUtil'
import { formatSynergy, formatSynergyColorId } from '@/utils/formatUtil'
import { getRecName } from '@/utils/recUtil'

import { StatCommonBox } from '../../StatCommon/StatCommonBox/StatCommonBox'
import { StatCommonFigure } from '../../StatCommon/StatCommonFigure/StatCommonFigure'
import { StatCommonIconChevron } from '../../StatCommon/StatCommonIconChevron/StatCommonIconChevron'

type Props = {
  rec: RecType
}

export const StatRecSynergy = ({ rec }: Props) => {
  const { page } = usePage()
  const featuredRec = page?.featured?.rec
  const {
    card: featuredCard,
    cardPartner: featuredPartner,
    loading: featuredLoading,
  } = useCardsRec({ rec: featuredRec })
  const { inclusion, numDecks, potentialDecks, synergy } = rec
  const fullName = getRecName(rec)
  const featuredName = getRecName(featuredRec)
  const featuredColorId = getColorId(featuredCard?.colorId, featuredPartner?.colorId)

  if (featuredLoading) return <StatCommonBox label='Synergy' loading />

  if (synergy === undefined || numDecks === undefined || potentialDecks === undefined || inclusion === undefined)
    return <StatCommonBox label='Synergy' />

  let variant: ComponentProps<typeof StatCommonIconChevron>['variant'] = 'neutral'
  if (synergy <= -0.5) variant = 'veryLow'
  else if (synergy <= -0.25) variant = 'low'
  else if (synergy <= -0.1) variant = 'slightlyLow'
  else if (synergy >= 0.5) variant = 'veryHigh'
  else if (synergy >= 0.25) variant = 'high'
  else if (synergy >= 0.1) variant = 'slightlyHigh'

  const popover = (
    <>
      <h2>
        <IconWithLabel iconBefore='wandMagicSparkles'>Synergy</IconWithLabel>
      </h2>
      <p>
        <strong>{fullName}</strong> has <strong>{formatSynergy(synergy)}% synergy</strong> with{' '}
        <strong>{featuredName}</strong> as a commander, which is the inclusion rate of this card in{' '}
        <strong>{featuredName}</strong> decks (<strong>{formatSynergy(inclusion)}%</strong>) minus its inclusion rate in
        all <Icon name={COLOR_ID_LABELS[featuredColorId].icon} /> decks{' '}
        <span className='nowrap'>
          (<strong>{formatSynergyColorId(numDecks, potentialDecks, synergy)}</strong>).
        </span>
      </p>
    </>
  )

  return (
    <StatCommonBox label='Synergy' popover={popover}>
      <StatCommonFigure icon={<StatCommonIconChevron variant={variant} />} suffix='%'>
        {formatSynergy(synergy)}
      </StatCommonFigure>
    </StatCommonBox>
  )
}
